// Selection management module for PPT Hero game
export default class SelectionManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Select all shapes
  selectAllShapes() {
    try {
      console.log('🔧 selectAllShapes() called - clearing selection first');
      console.log('🔧 Available shapes:', this.scene.allShapes.length);
      
      this.clearSelection();
      this.scene.selectedShapes = [...this.scene.allShapes];
      
      console.log('🔧 Selecting', this.scene.selectedShapes.length, 'shapes');
      this.scene.selectedShapes.forEach((shape, index) => {
        console.log(`🔧 Selected shape ${index}: x=${shape.x}, y=${shape.y}`);
      });
      
      this.updateSelectionVisuals();
      console.log('✅ selectAllShapes completed -', this.scene.selectedShapes.length, 'shapes selected');
      
      // Track the shortcut usage
      this.scene.trackShortcutUsed('ctrl,a');
      
    } catch (error) {
      console.error('Error selecting all shapes:', error);
    }
  }

  // Update selection visuals to match unified style
  updateSelectionVisuals() {
    try {
      // Reset all shapes to default appearance
      this.scene.allShapes.forEach(shape => {
        if (shape && shape.setStrokeStyle) {
          shape.setStrokeStyle(2, 0x000000, 0.8);
          if (shape.originalFillColor !== undefined) {
            shape.setFillStyle(shape.originalFillColor, 1.0);
          }
        }
      });
      
      // Highlight selected shapes with unified red border style
      this.scene.selectedShapes.forEach(shape => {
        if (shape && shape.setStrokeStyle) {
          shape.setStrokeStyle(2, 0xff0000); // Match all other selection styles
        }
      });
      
      // Update selection count display
      this.updateSelectionCount();
      
    } catch (error) {
      console.error('Error updating selection visuals:', error);
    }
  }

  // Update selection count display
  updateSelectionCount() {
    try {
      if (this.scene.selectionCountText) {
        const count = this.scene.selectedShapes.length;
        this.scene.selectionCountText.setText(`Selected: ${count}`);
        
        // Update color based on selection count
        if (count === 0) {
          this.scene.selectionCountText.setColor('#cccccc');
        } else if (count === 1) {
          this.scene.selectionCountText.setColor('#00ff88');
        } else {
          this.scene.selectionCountText.setColor('#ffaa00');
        }
      }
      
    } catch (error) {
      console.error('Error updating selection count:', error);
    }
  }

  // Get selected shapes
  getSelectedShapes() {
    return [...this.scene.selectedShapes];
  }

  // Check if any shapes are selected
  hasSelection() {
    return this.scene.selectedShapes.length > 0;
  }



  // Get selection bounds
  getSelectionBounds() {
    if (this.scene.selectedShapes.length === 0) return null;
    
    try {
      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;
      
      this.scene.selectedShapes.forEach(shape => {
        if (shape && shape.x !== undefined && shape.y !== undefined) {
          const halfWidth = shape.displayWidth / 2;
          const halfHeight = shape.displayHeight / 2;
          
          minX = Math.min(minX, shape.x - halfWidth);
          minY = Math.min(minY, shape.y - halfHeight);
          maxX = Math.max(maxX, shape.x + halfWidth);
          maxY = Math.max(maxY, shape.y + halfHeight);
        }
      });
      
      return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2
      };
      
    } catch (error) {
      console.error('Error getting selection bounds:', error);
      return null;
    }
  }

  // Move selection by offset
  moveSelection(offsetX, offsetY) {
    try {
      if (this.scene.selectedShapes.length === 0) return;
      
      this.scene.selectedShapes.forEach(shape => {
        if (shape && shape.x !== undefined && shape.y !== undefined) {
          shape.x += offsetX;
          shape.y += offsetY;
        }
      });
      
      // Track movement
      this.scene.trackKeystrokes();
      
    } catch (error) {
      console.error('Error moving selection:', error);
    }
  }

  // Scale selection
  scaleSelection(scaleX, scaleY) {
    try {
      if (this.scene.selectedShapes.length === 0) return;
      
      this.scene.selectedShapes.forEach(shape => {
        if (shape && shape.setScale) {
          const currentScaleX = shape.scaleX || 1;
          const currentScaleY = shape.scaleY || 1;
          shape.setScale(currentScaleX * scaleX, currentScaleY * scaleY);
        }
      });
      
      // Track scaling
      this.scene.trackKeystrokes();
      
    } catch (error) {
      console.error('Error scaling selection:', error);
    }
  }

  // Rotate selection
  rotateSelection(angle) {
    try {
      if (this.scene.selectedShapes.length === 0) return;
      
      this.scene.selectedShapes.forEach(shape => {
        if (shape && shape.setRotation) {
          const currentRotation = shape.rotation || 0;
          shape.setRotation(currentRotation + angle);
        }
      });
      
      // Track rotation
      this.scene.trackKeystrokes();
      
    } catch (error) {
      console.error('Error rotating selection:', error);
    }
  }

  // Duplicate selection
  duplicateSelection() {
    try {
      if (this.scene.selectedShapes.length === 0) return;
      
      const duplicatedShapes = [];
      
      this.scene.selectedShapes.forEach(shape => {
        if (shape && shape.x !== undefined && shape.y !== undefined) {
          const newShape = this.scene.createShape(
            shape.x + 20,
            shape.y + 20,
            shape.displayWidth,
            shape.displayHeight,
            shape.fillColor || 0xff0000
          );
          
          if (newShape) {
            duplicatedShapes.push(newShape);
            this.scene.allShapes.push(newShape);
          }
        }
      });
      
      // Select the duplicated shapes
      this.clearSelection();
      this.scene.selectedShapes = duplicatedShapes;
      this.updateSelectionVisuals();
      
      // Track duplication
      this.scene.trackKeystrokes();
      
    } catch (error) {
      console.error('Error duplicating selection:', error);
    }
  }

  // Delete selection
  deleteSelection() {
    try {
      if (this.scene.selectedShapes.length === 0) return;
      
      this.scene.selectedShapes.forEach(shape => {
        if (shape && shape.destroy) {
          // Remove from allShapes array
          const index = this.scene.allShapes.indexOf(shape);
          if (index > -1) {
            this.scene.allShapes.splice(index, 1);
          }
          
          // Destroy the shape
          shape.destroy();
        }
      });
      
      // Clear selection
      this.clearSelection();
      
      // Track deletion
      this.scene.trackKeystrokes();
      
    } catch (error) {
      console.error('Error deleting selection:', error);
    }
  }

  // Invert selection
  invertSelection() {
    try {
      const newSelection = [];
      
      this.scene.allShapes.forEach(shape => {
        if (!this.scene.selectedShapes.includes(shape)) {
          newSelection.push(shape);
        }
      });
      
      this.scene.selectedShapes = newSelection;
      this.updateSelectionVisuals();
      
    } catch (error) {
      console.error('Error inverting selection:', error);
    }
  }

  // Select by type
  selectByType(type) {
    try {
      this.clearSelection();
      
      this.scene.allShapes.forEach(shape => {
        if (shape && shape.getData && shape.getData('type') === type) {
          this.scene.selectedShapes.push(shape);
        }
      });
      
      this.updateSelectionVisuals();
      
    } catch (error) {
      console.error('Error selecting by type:', error);
    }
  }

  // Standardize heights - PowerPoint standard operation
  standardizeHeights() {
    console.log('📏 Standardizing heights to first selected shape');
    
    const selectedShapes = this.getSelectedShapes();
    if (selectedShapes.length < 2) {
      this.scene.showCenterNotification('Select reference shape first, then other shapes!', 'warning', 2000);
      return false;
    }
    
    // First shape is the reference
    const referenceShape = selectedShapes[0];
    const referenceHeight = referenceShape.displayHeight || referenceShape.height;
    
    console.log('📏 Reference shape height:', referenceHeight);
    console.log('📏 Standardizing', selectedShapes.length - 1, 'shapes to match');
    
    // Update all other selected shapes to match reference height
    selectedShapes.slice(1).forEach((shape, index) => {
      const oldHeight = shape.displayHeight || shape.height;
      shape.displayHeight = referenceHeight;
      shape.setDisplaySize(shape.displayWidth || shape.width, referenceHeight);
      console.log(`📏 Shape ${index + 1} height: ${oldHeight} → ${referenceHeight}`);
    });
    
    this.scene.showCenterNotification('✅ Heights Standardized!', 'success', 1500);
    return true;
  }

  // Standardize widths - PowerPoint standard operation  
  standardizeWidths() {
    console.log('📐 Standardizing widths to first selected shape');
    
    const selectedShapes = this.getSelectedShapes();
    if (selectedShapes.length < 2) {
      this.scene.showCenterNotification('Select reference shape first, then other shapes!', 'warning', 2000);
      return false;
    }
    
    // First shape is the reference
    const referenceShape = selectedShapes[0];
    const referenceWidth = referenceShape.displayWidth || referenceShape.width;
    
    console.log('📐 Reference shape width:', referenceWidth);
    console.log('📐 Standardizing', selectedShapes.length - 1, 'shapes to match');
    
    // Update all other selected shapes to match reference width
    selectedShapes.slice(1).forEach((shape, index) => {
      const oldWidth = shape.displayWidth || shape.width;
      shape.displayWidth = referenceWidth;
      shape.setDisplaySize(referenceWidth, shape.displayHeight || shape.height);
      console.log(`📐 Shape ${index + 1} width: ${oldWidth} → ${referenceWidth}`);
    });
    
    this.scene.showCenterNotification('✅ Widths Standardized!', 'success', 1500);
    return true;
  }

  // Check if heights have been standardized (for level completion)
  checkHeightStandardization(targetLevel = null) {
    if (targetLevel && this.scene.currentLevel !== targetLevel) return false;
    
    const allShapes = this.scene.allShapes || [];
    if (allShapes.length < 2) return false;
    
    // Find the triangle (reference shape) and squares
    const triangle = allShapes.find(shape => shape.getData && shape.getData('shapeType') === 'triangle');
    const squares = allShapes.filter(shape => shape.getData && shape.getData('shapeType') === 'square');
    
    if (!triangle || squares.length < 2) {
      console.log('📏 Checking standardization: Triangle or squares not found');
      return false;
    }
    
    const referenceHeight = triangle.displayHeight || triangle.height || 120;
    
    // Check if all squares have the same height as the triangle (with 5px tolerance)
    const allHeightsMatch = squares.every(square => {
      const squareHeight = square.displayHeight || square.height;
      const heightDifference = Math.abs(squareHeight - referenceHeight);
      return heightDifference <= 5; // 5px tolerance
    });
    
    console.log('📏 Height standardization check:', { 
      referenceHeight, 
      squareHeights: squares.map(s => s.displayHeight || s.height),
      allMatch: allHeightsMatch 
    });
    
    return allHeightsMatch;
  }
  
  // Check if widths have been standardized (for level completion)
  checkWidthStandardization(targetLevel = null) {
    if (targetLevel && this.scene.currentLevel !== targetLevel) return false;
    
    const allShapes = this.scene.allShapes || [];
    if (allShapes.length < 2) return false;
    
    // Find the circle (reference shape) and squares
    const circle = allShapes.find(shape => shape.getData && shape.getData('shapeType') === 'circle');
    const squares = allShapes.filter(shape => shape.getData && shape.getData('shapeType') === 'square');
    
    if (!circle || squares.length < 2) {
      console.log('📐 Checking standardization: Circle or squares not found');
      return false;
    }
    
    const referenceWidth = circle.displayWidth || circle.width || 130;
    
    // Check if all squares have the same width as the circle (with 5px tolerance)
    const allWidthsMatch = squares.every(square => {
      const squareWidth = square.displayWidth || square.width;
      const widthDifference = Math.abs(squareWidth - referenceWidth);
      return widthDifference <= 5; // 5px tolerance
    });
    
    console.log('📐 Width standardization check:', { 
      referenceWidth, 
      squareWidths: squares.map(s => s.displayWidth || s.width),
      allMatch: allWidthsMatch 
    });
    
    return allWidthsMatch;
  }
}

